/**
 * The pill, without a band.
 *
 * `design/DESIGN-SYSTEM.md` §2 specifies the BAND tag — "radius 999px, padding
 * 5px 12px, 13px 600, inline-flex, 7px `currentColor` dot, 6px gap" — and the
 * members screen uses the same box with no dot, in two tones: the Owner's role
 * on `--mdx-brand-tint`/`--mdx-brand-deep`, and the workspace scope on
 * `--mdx-nurture-tint`/`--mdx-slate`.
 *
 * NO DOT BY DEFAULT. The dot is what the band tag adds; a role and a workspace
 * name are not bands, and RULE-001 keeps that vocabulary clean. `dot` is opt-in
 * for the one pill on this screen that carries a STATE rather than a label —
 * "Invited 2 days ago", which the mockup draws on `--mdx-review-tint` with the
 * dot, because a pending invitation is a thing waiting on somebody. It is still
 * not a band: nothing here is scored.
 *
 * Story 4.1.
 */
import type { ReactNode } from 'react'

type Tone = 'brand' | 'neutral' | 'review' | 'hot' | 'error'

const TONE: Record<Tone, string> = {
  brand: 'bg-mdx-brand-tint text-mdx-brand-deep',
  neutral: 'bg-mdx-nurture-tint text-mdx-slate',
  review: 'bg-mdx-review-tint text-mdx-review-ink',
  // Story 6.1: a Published funnel, `funnels-list.html`. Still not a band —
  // RULE-001 keeps that word for a score — but it is the same green box, and a
  // second definition of it would be the thing that drifts.
  hot: 'bg-mdx-hot-tint text-mdx-hot-ink',
  /**
   * The one red box in the system, and the only place RULE-002 allows one:
   * "Red is reserved for errors and destructive actions." Drawn OUTLINED rather
   * than filled — white with an `--mdx-error` border and text — which is how
   * `editor-connect.html` renders a failed delivery, and it is the right
   * weight: a send that did not arrive is a fact to notice, not an alarm.
   *
   * Story 9.5.
   */
  error: 'border border-mdx-error bg-mdx-white text-mdx-error',
}

export function Pill({
  tone = 'neutral',
  dot = false,
  children,
  className = '',
}: {
  tone?: Tone
  dot?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={[
        'inline-flex shrink-0 items-center gap-mdx-6 rounded-mdx-pill',
        'px-mdx-12 py-mdx-5 text-mdx-13 font-mdx-semibold',
        TONE[tone],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {dot ? (
        <span aria-hidden="true" className="size-mdx-7 rounded-mdx-pill bg-current" />
      ) : null}
      {children}
    </span>
  )
}
